import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { format, parse } from 'date-fns';
// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult } from 'fuse.js';
import React from 'react';

import { useGetContactsQuery } from '@/api/v1';
import { DatePicker } from '@/components/atoms/DatePicker';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { Contact, SearchableContact } from '@/types/contacts';
import { CurrencyCodeType } from '@/types/currency';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { SimplifiedTransactionStatus, TransactionType } from '@/types/transaction';
import { noop } from '@/utility/noop';

interface Props {
  readonly filters: GetTransactionsFilters;
  onChange(filter: GetTransactionsFilters): void;
}

type ChangeFn = (event: React.ChangeEvent<HTMLInputElement>) => void;

export const TransactionsFilters: React.FC<Props> = (props: Props): React.ReactElement => {
  const customer = useCustomer();

  const { filters, onChange } = props;
  const { data: unfilteredRecipients = [] } = useGetContactsQuery(customer.beneficiaryId, {
    skip: customer.beneficiaryId === null,
  });

  const [recipientSearchKeyword, setRecipientSearchKeyword] = React.useState<string>('');
  const [data, setData] = React.useState<GetTransactionsFilters>(filters);

  const tr = useTranslator();
  const searchableUnfilteredContacts = React.useMemo(
    (): readonly SearchableContact[] =>
      unfilteredRecipients.map(
        (contact: Contact): SearchableContact => ({
          id: contact.id,
          displayName: Contact.getName(contact),
        }),
      ),
    [unfilteredRecipients],
  );

  const fuse = React.useMemo((): Fuse<SearchableContact> => {
    return new Fuse(searchableUnfilteredContacts, {
      keys: ['displayName'],
    });
  }, [searchableUnfilteredContacts]);

  const recipients = React.useMemo((): readonly SearchableContact[] => {
    if (recipientSearchKeyword.trim() !== '') {
      const results = fuse.search(recipientSearchKeyword);
      return results.map((result: FuseResult<SearchableContact>): SearchableContact => result.item);
    }

    return searchableUnfilteredContacts;
  }, [fuse, recipientSearchKeyword, searchableUnfilteredContacts]);

  const handleDateChange = React.useCallback(
    (field: keyof GetTransactionsFilters): ((date: Date | null) => void) => {
      return (date: Date | null): void => {
        if (!date) {
          setData(
            (localState: GetTransactionsFilters): GetTransactionsFilters => ({
              ...localState,
              [field]: '',
            }),
          );
        } else {
          setData(
            (localState: GetTransactionsFilters): GetTransactionsFilters => ({
              ...localState,
              [field]: format(date, internalFormat),
            }),
          );
        }
      };
    },
    [],
  );

  const handleChange = React.useCallback((field: keyof GetTransactionsFilters): ChangeFn => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;

      setData(
        (localState: GetTransactionsFilters): GetTransactionsFilters => ({
          ...localState,
          [field]: value,
        }),
      );
    };
  }, []);

  const fromDate = React.useMemo(
    (): Date | null =>
      data.fromDate === '' ? null : parse(data.fromDate, internalFormat, new Date()),
    [data.fromDate],
  );

  const toDate = React.useMemo(
    (): Date | null => (data.toDate === '' ? null : parse(data.toDate, internalFormat, new Date())),
    [data.toDate],
  );

  const onApply = React.useCallback((): void => {
    onChange(data);
  }, [onChange, data]);

  const fromDateResetRef = React.useRef<VoidFunction>(noop);
  const toDateResetRef = React.useRef<VoidFunction>(noop);

  const onReset = React.useCallback((): void => {
    const { current: resetFromDate } = fromDateResetRef;
    const { current: resetToDate } = toDateResetRef;

    resetFromDate?.();
    resetToDate?.();

    setData(GetTransactionsFilters.initial());

    onChange(GetTransactionsFilters.initial());
  }, [onChange]);

  const onSearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: keyword } = event.target;
      setRecipientSearchKeyword(keyword);
    },
    [],
  );

  return (
    <Stack>
      <Box sx={styles.container}>
        <TextField
          select={true}
          label={tr('TRANSACTION_TYPE')}
          value={data.type}
          fullWidth={true}
          onChange={handleChange('type')}>
          <MenuItem value={TransactionType.ALL}>{tr('ALL')}</MenuItem>
          <MenuItem value={TransactionType.TRANSFER}>{tr('TRANSFER')}</MenuItem>
          <MenuItem value={TransactionType.WITHDRAWAL}>{tr('WITHDRAWAL')}</MenuItem>
          <MenuItem value={TransactionType.DEPOSIT}>{tr('DEPOSIT')}</MenuItem>
        </TextField>
        <TextField
          select={true}
          label={tr('STATUS')}
          value={data.status}
          fullWidth={true}
          onChange={handleChange('status')}>
          <MenuItem value={SimplifiedTransactionStatus.ALL}>{tr('ALL')}</MenuItem>
          <MenuItem value={SimplifiedTransactionStatus.IN_PROGRESS}>{tr('IN_PROGRESS')}</MenuItem>
          <MenuItem value={SimplifiedTransactionStatus.COMPLETED}>{tr('COMPLETED')}</MenuItem>
          <MenuItem value={SimplifiedTransactionStatus.FAILED}>{tr('FAILED')}</MenuItem>
          <MenuItem value={SimplifiedTransactionStatus.EXPIRED}>{tr('EXPIRED')}</MenuItem>
        </TextField>
        <DatePicker
          label={tr('FROM_DATE', displayFormat.toUpperCase())}
          reset={fromDateResetRef}
          value={fromDate}
          format={displayFormat}
          onChange={handleDateChange('fromDate')}
        />
        <DatePicker
          label={tr('TO_DATE', displayFormat.toUpperCase())}
          reset={toDateResetRef}
          value={toDate}
          format={displayFormat}
          onChange={handleDateChange('toDate')}
        />
        <TextField
          label={tr('AMOUNT')}
          value={data.amount}
          fullWidth={true}
          onChange={handleChange('amount')}
        />
        <TextField
          select={true}
          label={tr('CURRENCY_TYPE')}
          value={data.currency}
          fullWidth={true}
          onChange={handleChange('currency')}>
          <MenuItem value={CurrencyCodeType.ANY}>{tr('ANY')}</MenuItem>
          <MenuItem value={CurrencyCodeType.USD}>USD</MenuItem>
          <MenuItem value={CurrencyCodeType.BRL}>BRL</MenuItem>
          <MenuItem value={CurrencyCodeType.MXN}>MXN</MenuItem>
        </TextField>
        <TextField
          label={tr('ID #')}
          value={data.id}
          fullWidth={true}
          onChange={handleChange('id')}
        />
        <TextField
          select={true}
          label={tr('RECIPIENT')}
          value={data.recipientId}
          fullWidth={true}
          onChange={handleChange('recipientId')}>
          <SearchMenuItem onChange={onSearchKeywordChange} />
          <MenuItem value="*">{tr('ANY')}</MenuItem>
          {recipients.map(
            (recipient: SearchableContact): React.ReactElement => (
              <MenuItem key={recipient.id} value={recipient.id}>
                {recipient.displayName}
              </MenuItem>
            ),
          )}
        </TextField>
      </Box>
      <Stack alignItems="center" mt={6} gap={3}>
        <Button variant="contained" fullWidth onClick={onApply}>
          {tr('APPLY')}
        </Button>
        <Button variant="text" sx={styles.textButton} disableRipple onClick={onReset}>
          {tr('RESET')}
        </Button>
      </Stack>
    </Stack>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: '1fr 1fr',
  },
  textButton: {
    color: 'text.primary',
  },
  spanned: {
    gridColumn: 'span 2',
  },
};

const displayFormat = 'dd/MM/yyyy';
const internalFormat = 'yyyy-MM-dd';
